import { useContext, useState, useEffect, useRef } from 'react';
import {
  appendScript,
  detectBrowser,
  formatPhoneNumber,
  formatNumber,
} from '../../helpers';

import DividerLine from '../../components/DividerLine';
import Input from '../../components/Input';
import CustomSelect from '../../components/CustomSelect';
import Button from '../../components/Button';

import iconCaret from './../../images/success/icon-caret.svg';
import iconFacebook from './../../images/success/facebook.svg';
import iconYoutube from './../../images/success/youtube.svg';
import iconTwitter from './../../images/success/twitter.svg';
import iconLinkedIn from './../../images/success/linkedin.svg';

import iconFacebookWhite from './../../images/success/facebook_white.svg';
import iconYoutubeWhite from './../../images/success/youtube_white.svg';
import iconTwitterWhite from './../../images/success/twitter_white.svg';
import iconLinkedInWhite from './../../images/success/linkedin_white.svg';
import logoEnphaseWhite from './../../images/success/logo_enphase_white.png';

import iconInstallerSolar from './../../images/success/icon_installer_solar.svg';
import iconInstallerStorage from './../../images/success/icon_installer_storage.svg';
import iconInstallerLocation from './../../images/success/icon_installer_location.svg';
import iconInstallerTierPlatinum from './../../images/success/icon_installer_tier_platinum.svg';
import iconInstallerTierGold from './../../images/success/icon_installer_tier_gold.svg';
import iconInstallerTierSilver from './../../images/success/icon_installer_tier_silver.svg';

import ratingStarEmpty from './../../images/success/rating_star_empty.svg';
import ratingStarFilled from './../../images/success/rating_star_filled.svg';
import ratingStarSemiFilled from './../../images/success/rating_star_semifilled.svg';

import iconCircle from './../../images/circle.svg';
import iconCircleChecked from './../../images/circle_check.svg';
import iconSubmitted from '../../images/success/icon_installer_submitted.svg';

import { h3Class, h4Class, h5Class, listClass } from './helpers';
import AppContext from '../../contexts/AppContext';
import ConfigContext from '../../contexts/ConfigContext';
import DataContext from '../../contexts/DataContext';

import { LeadData, RatingStar } from './types';
import { useSearchParams } from 'react-router-dom';

declare const sp: any;

// Additional components
export const LoadingOverlay = ({
  message = null,
  showRipple = true,
  showSmallText = false,
}: {
  message?: string | null;
  showRipple?: boolean;
  showSmallText?: boolean;
}) => {
  return (
    <div className="loader-screen">
      <div
        className={`loader-screen-overlay ${
          detectBrowser() !== 'safari' ? 'box-shadow' : ''
        }`}></div>
      <div className="px-[30px] md:px-[60px] loader-screen-content">
        <div className="loader-screen-message">
          {showRipple && (
            <div className="wrapper-cover-parent">
              <div className="lds-ripple">
                <div></div>
                <div></div>
              </div>
            </div>
          )}
          <div
            className={`wrapper-cover-parent text-[14px] ${
              showSmallText ? 'md:text-[15px]' : 'md:text-[20px]'
            }`}>
            <h3>
              {message ? (
                `${message}`
              ) : (
                <>
                  Personalizing your
                  <br />
                  system
                  <br />
                  recommendations...
                </>
              )}
            </h3>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ToggleSelectorV1 = ({
  options,
  selectedOption = 'loan',
  setSelectedOption,
}: {
  options: string[];
  selectedOption?: string;
  setSelectedOption: any;
}) => {
  const { isDebrandedVersion } = useContext(ConfigContext);
  const backColor = isDebrandedVersion ? 'bg-[#5ecf7f]' : 'bg-enph-orange';

  return (
    <div className="w-[124px] md:w-[140px] h-[28px] md:h-[34px] bg-enph-grey-04 rounded-[56px] p-[2px] flex">
      <button
        className={`text-black text-[14px] capitalize ${
          selectedOption === options[0] ? backColor : ''
        } rounded-full basis-1/2`}
        onClick={() => {
          setSelectedOption(options[0]);
        }}>
        {options[0]}
      </button>
      <button
        className={`text-black text-[14px] capitalize ${
          selectedOption === options[1] ? backColor : ''
        } rounded-full basis-1/2`}
        onClick={() => {
          setSelectedOption(options[1]);
        }}>
        {options[1]}
      </button>
    </div>
  );
};

export const ToggleSelector = ({
  options,
  selectedOption = 'loan',
  setSelectedOption,
  useWhiteBackground = false,
}: {
  options: string[];
  selectedOption?: string;
  setSelectedOption: any;
  useWhiteBackground?: boolean;
}) => {
  const { isDebrandedVersion } = useContext(ConfigContext);
  const backColor = isDebrandedVersion ? 'bg-[#5ecf7f]' : 'bg-enph-orange';

  return (
    <div
      className={`w-[124px] md:w-[248px] h-[28px] md:h-[48px] rounded-[56px] p-[2px] md:p-[4px] flex ${
        useWhiteBackground ? 'bg-white' : 'bg-enph-grey-04'
      }`}>
      <button
        className={`text-black text-[12px] md:text-[18px] capitalize ${
          selectedOption === options[0] ? backColor : ''
        } rounded-full basis-1/2`}
        onClick={() => {
          setSelectedOption(options[0]);
        }}>
        {options[0]}
      </button>
      <button
        className={`text-black text-[12px] md:text-[18px] capitalize ${
          selectedOption === options[1] ? backColor : ''
        } rounded-full basis-1/2`}
        onClick={() => {
          setSelectedOption(options[1]);
        }}>
        {options[1]}
      </button>
    </div>
  );
};

export const LinkButton = ({
  text,
  url,
  target = '_self',
  className,
  small = false,
}: {
  text: string;
  url: string;
  target?: string;
  className?: string;
  small?: boolean;
}) => {
  return (
    <a
      href={url}
      className={`bg-black text-white ${
        small
          ? 'text-[10px] md:text-[14px] py-2 md:py-3 px-[36px]'
          : 'text-[12px] md:text-[18px] py-3 md:py-4 px-[56px]'
      } rounded-full w-full md:w-auto max-w-[328px] md:max-w-[400px] hover:opacity-80 inline-block ${className}`}
      target={target}>
      {text}
    </a>
  );
};

export const GoSolarReason = ({
  reason,
  className,
}: {
  reason: { title: string; description: string; iconUrl: string };
  className: string;
}) => {
  const { title, description, iconUrl } = reason;
  const capitalizeFistLetter = (text: string): string => {
    return text.length
      ? `${text[0].toUpperCase()}${text
          .substring(1, text.length)
          .toLowerCase()}`
      : text;
  };

  return (
    <div className={`flex gap-3 md:gap-x-6 ${className}`}>
      <div>
        <div className="bg-white w-[50px] md:w-[90px] h-[50px] md:h-[90px] rounded-full flex justify-center items-center">
          <img
            alt={title}
            src={iconUrl}
            className="w-[28px] md:w-10 h-[28px] md:h-10"
          />
        </div>
      </div>
      <div className="grow">
        <p className={`${h5Class} mb-2 md:mb-4`}>
          {capitalizeFistLetter(title)}
        </p>
        <p>{description}</p>
      </div>
    </div>
  );
};

export const Faqs = ({ faqs }: any) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <div>
      {faqs.map((faq: any, index: number) => (
        <div
          className={`flex flex-col py-[30px] md:py-[32px] ${
            index < faqs.length - 1 ? 'border-b border-b-enph-grey-02' : ''
          }`}
          key={index}>
          <div
            className="flex justify-between cursor-pointer items-baseline"
            onClick={() => {
              if (index !== selectedIndex) {
                setSelectedIndex(index);
              } else {
                setSelectedIndex(-1);
              }
            }}>
            <p className="text-[17px] md:text-[24px] mr-[20px] md:mr-[40px]">
              {faq.question}
            </p>
            <p
              className="text-[28px] md:text-[40px]"
              style={{ fontWeight: 200 }}>
              {selectedIndex === index ? '-' : '+'}
            </p>
          </div>
          <div className={selectedIndex === index ? 'block' : 'hidden'}>
            {faq.answer.startsWith('<p>') ? (
              <div
                className="mt-[30px] md:mt-[32px]"
                dangerouslySetInnerHTML={{ __html: faq.answer }}></div>
            ) : (
              <p className="mt-[30px] md:mt-[32px]">{faq.answer}</p>
            )}
            {faq.footNote &&
              (faq.footNote.startsWith('<p>') ? (
                <div
                  className="text-[12px] md:text-[17px] mt-4 md:mt-6"
                  dangerouslySetInnerHTML={{ __html: faq.footNote }}></div>
              ) : (
                <p className="text-[12px] md:text-[17px] mt-4 md:mt-6">
                  {faq.footNote}
                </p>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Content helpers
export const Title = ({
  address,
  className,
  breakLine = false,
  bold = false,
}: {
  address: string;
  className?: string;
  breakLine?: boolean;
  bold?: boolean;
}) => (
  <p className={className}>
    Preliminary estimate for {breakLine && <br></br>}
    <span className={bold ? 'font-bold' : ''}>{address}</span>
  </p>
);

export const WhatsNextContent = ({
  className,
  showRentWarning = false,
  pdfLink,
  leadData,
}: {
  className?: string;
  showRentWarning?: boolean;
  pdfLink?: string;
  leadData: LeadData;
}) => {
  const { isDebrandedVersion, features } = useContext(ConfigContext);
  const { slfApiUrl } = useContext(AppContext);
  const { data } = useContext(DataContext);
  const [installerList, setInstallerList] = useState([]);
  const [showInstallerList, setShowInstallerList] = useState(
    !!(
      isDebrandedVersion &&
      features.showLocalInstallerListingOnSuccess &&
      installerList.length
    )
  );

  const [searchParams] = useSearchParams();
  const searchParamsEntries = Object.fromEntries(searchParams.entries());

  const setNoInstallersFlag = () => {
    const noInstallersUrl = `${slfApiUrl}/api/user/update_lead_meta/${searchParamsEntries.lid}/no_installers_to_list/1`;
    appendScript(noInstallersUrl);
  };

  useEffect(() => {
    if (isDebrandedVersion && features.showLocalInstallerListingOnSuccess) {
      const fetchInstallers = async () => {
        const data = await fetch(
          `${slfApiUrl}/api/ein/get_buyers/${leadData.zipCode}`
        );
        const jsonResponse = await data.json();
        setInstallerList(jsonResponse);
        if (!jsonResponse || !jsonResponse.length) {
          setNoInstallersFlag();
        }
      };

      fetchInstallers().catch((e) => {
        setNoInstallersFlag();
      });
    }
  }, []);

  useEffect(() => {
    if (installerList.length) {
      setShowInstallerList(true);
    }
  }, [installerList]);

  const textContent = (
    <>
      {showInstallerList && (
        <p className={`${h4Class} mb-2 md:mb-4`}>
          Select installers and get quotes
        </p>
      )}
      <p className="mb-4 md:mb-[20px] ">
        For optimal solar savings, we recommend comparing quotes from 2 to 3
        solar installation partners. Our trusted solar installation partners
        will contact you soon to customize your quote.
        {!isDebrandedVersion && (
          <span>
            <br></br>
            <br></br>
            {data.isEnphaseCustomer
              ? 'Click below to search for Enphase certified installers in your local area.'
              : 'You can also click below to search for Enphase certified solar installation partners that service your area.'}
          </span>
        )}
      </p>
      {!isDebrandedVersion && (
        <div>
          <a
            href="https://enphase.com/installer-locator"
            target="_blank"
            rel="noreferrer"
            className="text-black text-[14px] font-bold border border-black px-8 py-3 rounded-full hover:opacity-70">
            {data.isEnphaseCustomer
              ? 'Find an installer'
              : 'Find a solar installation partner'}
          </a>
        </div>
      )}
      {showRentWarning && (
        <div
          className={`text-enph-red-functional text-[12px] md:text-[18px] bg-enph-grey-04 rounded-[10px] p-4 md:p-[20px] my-10 md:my-[60px] ${className}`}>
          Installing solar is a significant property upgrade and would require
          your landlord’s permission. We recommend that you share this{' '}
          <a
            href={
              isDebrandedVersion
                ? window.location.href.replace('success', '')
                : pdfLink
            }
            target="_blank"
            rel="noreferrer"
            className="underline">
            preliminary estimate
          </a>{' '}
          with your landlord or owner of the property and talk to them about
          installing a solar system. Apart from the immediate benefit you will
          receive, the value of their home increases significantly. It's a
          win-win for everyone!
        </div>
      )}
      <p className={`${h4Class} mt-16 mb-2 md:mb-4`}>
        Once you pick a solar installation partner of your choice, they will
        help you with:
      </p>
      <div>
        <ul className={listClass}>
          <li>Conducting a survey of your house</li>
          <li>Finalizing the system design and financing</li>
          <li>Applying for permits with the city</li>
          <li>Installation and activation</li>
        </ul>
        <br></br>
        <p>This entire process will take about 1 to 3 months to complete.</p>
      </div>
    </>
  );

  return (
    <div className={className}>
      <p className={`${h3Class} mb-4 md:mb-10`}>What happens next?</p>
      {!showInstallerList && textContent}
      {showInstallerList && (
        <div className="flex flex-col md:flex-row gap-x-12">
          <div className="basis-1/2 grow-0">
            <Installers installerList={installerList} leadData={leadData} />
          </div>
          <div className="basis-1/2 grow-0">{textContent}</div>
        </div>
      )}
    </div>
  );
};

export const PanelsWarning = ({ className = '' }: { className?: string }) => {
  return (
    <p
      className={`text-enph-red-functional text-[12px] md:text-[18px] bg-enph-grey-04 rounded-[10px] p-4 md:p-[20px] my-4 md:my-5 ${className}`}>
      This configuration may not be supported by your roof size. Our certified
      solar installation partners will get in touch and help you identify the
      optimal system size as per your roof.
    </p>
  );
};

export const RentWarningHeader = ({
  className = '',
}: {
  className?: string;
}) => {
  return (
    <p
      className={`text-enph-red-functional text-[12px] md:text-[18px] bg-enph-grey-04 rounded-[10px] p-4 md:p-[20px] my-4 md:my-5 ${className}`}>
      Installing solar is a significant property upgrade and would require your
      landlord’s permission.
    </p>
  );
};

export const Nem3Error = ({ className = '' }: { className?: string }) => {
  return (
    <p
      className={`text-enph-red-functional text-[12px] md:text-[18px] bg-enph-grey-04 rounded-[10px] p-4 md:p-[20px] my-4 md:my-5 ${className}`}>
      The calculations are based on NEM 2.0 tariff structure. For a more up to
      date tariff structure (NEM 3.0) please rerun your estimates.
    </p>
  );
};

export const Nem3BillBar = ({
  isDebrandedVersion = false,
  percentage = 100,
}: {
  isDebrandedVersion?: boolean;
  percentage?: number;
}) => {
  return (
    <div className={`h-2.5 rounded-full relative overflow-hidden bg-[#F4F3F0]`}>
      <div
        className={`${
          isDebrandedVersion ? 'bg-[#5ecf7f]' : 'bg-enph-orange'
        } h-full absolute left-0 top-0 rounded-full`}
        style={{
          width: ` ${percentage || 0}%`,
          transition: 'width 0.5s',
        }}></div>
    </div>
  );
};

export const FooterV1 = () => {
  const { isDebrandedVersion } = useContext(ConfigContext);

  const links = isDebrandedVersion
    ? []
    : [
        {
          url: 'https://www.facebook.com/EnphaseEnergy/',
          icon: iconFacebookWhite,
        },
        {
          url: 'https://www.youtube.com/user/EnphaseEnergy/',
          icon: iconYoutubeWhite,
        },
        { url: 'https://twitter.com/Enphase/', icon: iconTwitterWhite },
        {
          url: 'https://www.linkedin.com/company/enphase-energy/',
          icon: iconLinkedInWhite,
        },
      ];

  return (
    <div className="p-6 md:px-20 bg-enph-dark-grey text-[10px] lg:text-[13px] text-white">
      <div className="max-w-[1060px] m-auto mt-[40px]">
        <img alt="" src={logoEnphaseWhite} className="h-[20px]" />
        <div>
          <br></br>
          <p>
            Copyright © {new Date().getFullYear()} Enphase Energy. All rights
            reserved.{' '}
            <a
              href="https://enphase.com/legal/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="underline">
              Privacy
            </a>
            ,{' '}
            <a
              href="https://enphase.com/legal/terms-of-service"
              target="_blank"
              rel="noreferrer"
              className="underline">
              Terms & Conditions
            </a>
            ,{' '}
            <button
              type="button"
              onClick={() => sp.showPrivacyBanner()}
              className="underline">
              Settings / Do Not Sell of Share My Personal Information
            </button>
            ,{' '}
            <a
              href={`${
                isDebrandedVersion
                  ? '/contact-us'
                  : 'https://support.enphase.com/s/owner-home'
              }`}
              target="_blank"
              rel="noreferrer"
              className="underline">
              Contact
            </a>
          </p>
          <br></br>
          <p>47281 Bayside Pkwy, Fremont, CA 94538, USA</p>
        </div>
        <div className="flex flex-row-reverse">
          <div className="flex">
            {links.map((link, index) => (
              <a
                key={index}
                className="w-6 mt-[10px] lg:mt-0 lg:w-8"
                target="_blank"
                href={link.url}
                rel="noreferrer">
                <img
                  className="social-icon m-auto w-full"
                  src={link.icon}
                  alt="social"
                />
              </a>
            ))}
          </div>
        </div>
        <DividerLine smallMarging={true}></DividerLine>
        <div className="flex gap-y-4 lg:gap-y-0 justify-between">
          <a
            href="https://enphase.com/legal/terms-of-service"
            target="_blank"
            rel="noreferrer">
            Terms of Service
          </a>
          <a
            href="https://enphase.com/legal/privacy-policy"
            target="_blank"
            rel="noreferrer">
            Privacy
          </a>
          <button type="button" onClick={() => sp.showPrivacyBanner()}>
            Notification preferences / unsubscribe
          </button>
        </div>
      </div>
    </div>
  );
};

export const Footer = () => {
  const { isDebrandedVersion } = useContext(ConfigContext);
  const { deBrandedDomain } = useContext(AppContext);

  const links = isDebrandedVersion
    ? []
    : [
        { url: 'https://www.facebook.com/EnphaseEnergy/', icon: iconFacebook },
        {
          url: 'https://www.youtube.com/user/EnphaseEnergy/',
          icon: iconYoutube,
        },
        { url: 'https://twitter.com/Enphase/', icon: iconTwitter },
        {
          url: 'https://www.linkedin.com/company/enphase-energy/',
          icon: iconLinkedIn,
        },
      ];

  return (
    <div className="p-6 md:px-20 bg-white text-[11px] md:text-[12px]">
      <div className="max-w-[1060px] m-auto">
        <div className="flex justify-between">
          <div className="flex">
            {links.map((link, index) => (
              <a
                key={index}
                className="w-8"
                target="_blank"
                href={link.url}
                rel="noreferrer">
                <img
                  className="social-icon m-auto w-full"
                  src={link.icon}
                  alt="social"
                />
              </a>
            ))}
          </div>
          <button
            type="button"
            className="text-enph-grey-02"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}>
            <span>back to top</span>
            <img
              alt="back to top"
              src={iconCaret}
              className="inline-block ml-[10px]"></img>
          </button>
        </div>
        <DividerLine smallMarging={true}></DividerLine>
        <div className="flex justify-between flex-col lg:flex-row text-center gap-y-4 lg:gap-y-0 mt-[20px] text-enph-grey-02 lg:text-enph-dark-grey">
          <div>
            Copyright © {new Date().getFullYear()}{' '}
            {isDebrandedVersion
              ? deBrandedDomain.copyrightText
              : 'Enphase Energy'}
            . All rights reserved.
          </div>
          <div className="flex flex-col lg:flex-row gap-y-4 lg:gap-y-0">
            <div>
              <a
                href={`${
                  isDebrandedVersion
                    ? '/tos'
                    : 'https://enphase.com/legal/terms-of-service'
                }`}
                target="_blank"
                rel="noreferrer"
                className="mx-[8px]">
                Terms
              </a>
              <a
                href={`${
                  isDebrandedVersion
                    ? '/tos#privacy'
                    : 'https://enphase.com/legal/privacy-policy'
                }`}
                target="_blank"
                rel="noreferrer"
                className="mx-[8px]">
                Privacy
              </a>
              {!isDebrandedVersion && (
                <a
                  href="https://enphase.com/legal/accessibility-statement"
                  target="_blank"
                  rel="noreferrer"
                  className="mx-[8px]">
                  Accessibility Statement
                </a>
              )}
            </div>
            <div>
              <button
                type="button"
                onClick={() => sp.showPrivacyBanner()}
                className="mx-[8px]">
                Settings / Do Not Sell or Share My Personal Information
              </button>
              <a
                href={`${
                  isDebrandedVersion
                    ? '/contact-us'
                    : 'https://support.enphase.com/s/owner-home'
                }`}
                target="_blank"
                rel="noreferrer"
                className="mx-[8px]">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Installers = ({
  installerList,
  leadData,
}: {
  installerList: any;
  leadData: LeadData;
}) => {
  const [openedIndex, setOpenedIndex] = useState(-1);
  const [installers, setInstallers] = useState([...installerList]);
  const [submitNumber, setSubmitNumber] = useState(1);

  const installerSelected = (id: string) => {
    const newInstallers = [...installers];
    const installer = newInstallers.find((inst) => inst.id === id);
    if (installer) {
      installer.selected = !installer.selected;
      setInstallers(newInstallers);
    }
  };

  const installerSubmitted = (id: string) => {
    const newInstallers = [...installers];
    const installer = newInstallers.find((inst) => inst.id === id);
    if (installer) {
      installer.submitted = true;
      setInstallers(newInstallers);
    }
  };

  return (
    <div>
      {installers.map((installer: any, index: number) => {
        const otherInstallers = installers.filter(
          (inst) => !inst.submitted && inst.id !== installer.id
        );
        return (
          <Installer
            key={index}
            installer={installer}
            otherInstallersList={otherInstallers}
            leadData={leadData}
            isOpen={openedIndex === index}
            onOpened={() => {
              setOpenedIndex(index);
            }}
            onClosed={() => {
              setOpenedIndex(-1);
            }}
            setInstallerSubmitted={installerSubmitted}
            setInstallerSelected={installerSelected}
            submitNumber={submitNumber}
            setSubmitNumber={setSubmitNumber}
          />
        );
      })}
    </div>
  );
};

const Installer = ({
  installer,
  otherInstallersList,
  leadData,
  isOpen = false,
  onOpened,
  onClosed,
  setInstallerSubmitted,
  setInstallerSelected,
  submitNumber,
  setSubmitNumber,
}: {
  installer: any;
  otherInstallersList: any;
  leadData: LeadData;
  isOpen?: boolean;
  onOpened: () => void;
  onClosed: () => void;
  setInstallerSubmitted: (id: string) => void;
  setInstallerSelected: (id: string) => void;
  submitNumber: number;
  setSubmitNumber: (number: number) => void;
}) => {
  const { slfApiUrl } = useContext(AppContext);

  const [fullName, setFullName] = useState(leadData.fullName);
  const [email, setEmail] = useState(leadData.email);
  const [phoneNumber, setPhoneNumber] = useState(leadData.phoneNumber);
  const callOptions = [
    { value: 'morning', label: 'Morning' },
    { value: 'afternoon', label: 'Afternoon' },
    { value: 'evening', label: 'Evening' },
  ];
  const [callAt, setCallAt] = useState({ value: 'morning', label: 'Morning' });

  const [nameHaveError, setNameHaveError] = useState(false);
  const [emailHaveError, setEmailHaveError] = useState(false);
  const [phoneHaveError, setPhoneHaveError] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const handleNameInput = (e: any) => {
    const name = e.target.value.trim();
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      setFullName(name);
      setNameHaveError(false);
    } else {
      setFullName('');
      setNameHaveError(true);
    }
  };

  const validateEmail = (email: string): boolean => {
    return !!String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      )?.length;
  };

  const handleEmailInput = (e: any) => {
    const email = e.currentTarget.value;
    if (validateEmail(email)) {
      setEmail(email);
      setEmailHaveError(false);
    } else {
      setEmail('');
      setEmailHaveError(true);
    }
  };

  const handlePhoneInput = (e: any) => {
    const number = e.target.value.trim();
    const realNumber = number.replace(/-|\(|\)|\s/g, '');

    if (realNumber.length >= 10) {
      if (realNumber.length > 10) {
        return;
      }
      setPhoneHaveError(false);
      const formattedPhoneNumber = formatPhoneNumber(number);
      setPhoneNumber(formattedPhoneNumber);
    } else {
      const formattedPhoneNumber = formatPhoneNumber(number);
      setPhoneNumber(formattedPhoneNumber);
      setPhoneHaveError(true);
    }
  };

  // const postInstaller = async(id: string, leadInfo: any) => {
  //   try{
  //     const data = new FormData();
  //     for (let key in leadInfo) {
  //       data.append(key, leadInfo[key]);
  //     }

  //     const rawResponse = await fetch(`${slfApiUrl}/api/ein/send_mail/${id}`, {
  //       method: 'POST',
  //       body: data
  //     });
  //     const content = await rawResponse.json();
  //     return content;
  //   } catch {
  //     return {status: false};
  //   }
  // }

  const submit = () => {
    setSubmitting(true);
    const installerNames = [];

    installerNames.push(installer.company_name);
    setInstallerSubmitted(installer.id);

    otherInstallersList
      .filter((inst: any) => inst.selected)
      .forEach((inst: any) => {
        installerNames.push(inst.company_name);
        setInstallerSubmitted(inst.id);
      });

    const installersDataUrl = `${slfApiUrl}/api/user/update_lead_meta/${
      leadData.id
    }/requested_quote_from_${submitNumber}/${encodeURIComponent(
      installerNames.join(', ').replace('*', '')
    )}`;
    appendScript(installersDataUrl);
    setSubmitNumber(submitNumber + 1);

    onClosed();
    setTimeout(scrollToRef?.current?.scrollIntoView(true));
    setSubmitting(false);

    // setSubmitting(true);
    // const leadInfo = {fullName, email, phoneNumber, callAt: callAt.value};
    // const otherInstallerIds = otherInstallersList.filter((installer: any) => installer.selected).map((installer: any) => installer.id);
    // const mainInstallerResult = await postInstaller(installer.id, leadInfo);
    // otherInstallerIds.forEach(async (id: string) => {
    //   const response = await postInstaller(id, leadInfo);
    //   if (response.status) {
    //     setInstallerSubmitted(id);
    //   }
    // });

    // if (mainInstallerResult.status) {
    //   setInstallerSubmitted(installer.id);
    //   onClosed();
    //   setTimeout(
    //     scrollToRef?.current?.scrollIntoView(true)
    //   );
    // } else {
    //   setSubmitError(true);
    // }
    // setSubmitting(false);
  };

  const scrollToRef: any = useRef(null);

  return (
    <div
      className="py-5 px-10 bg-[#FAF6EF] rounded-[24px] mb-6"
      ref={scrollToRef}>
      <div className="flex flex-col xl:flex-row">
        <div className="xl:basis-2/3">
          <div className="flex">
            <div>
              <img
                alt=""
                className="mr-2"
                src={
                  installer.tier === '1'
                    ? iconInstallerTierPlatinum
                    : installer.tier === '2'
                    ? iconInstallerTierGold
                    : iconInstallerTierSilver
                }
              />
            </div>
            <div className="flex flex-col">
              <p className="text-[16px] font-bold">{installer.company_name}</p>
              <p className="text-[12px] text-enph-grey-02">
                Enphase {installer.tier_text} Installer
              </p>
            </div>
          </div>
          <div className="text-[14px] flex mt-6">
            <img alt="" src={iconInstallerLocation} className="mr-1" />
            <p className="capitalize">
              {installer.address}, {installer.city}, {installer.state}{' '}
              {installer.zip}
            </p>
          </div>
          <div className="text-[14px] flex items-center mt-2">
            {installer.is_solar_installer === '1' && (
              <>
                <img
                  alt=""
                  src={iconInstallerSolar}
                  className="pb-[3px] mr-1"
                />
                <p>Solar</p>
              </>
            )}
            {installer.is_storage_installer === '1' && (
              <>
                <img
                  alt=""
                  src={iconInstallerStorage}
                  className="pb-[3px] mr-1 ml-8"
                />
                <p>Storage</p>
              </>
            )}
          </div>
        </div>
        <div className="xl:basis-1/3 flex justify-end">
          <InstallerRating
            rating={Number(installer.goog_ratings)}></InstallerRating>
        </div>
      </div>
      <div className={`mt-4 ${isOpen ? 'hidden' : ''}`}>
        {!installer.submitted && (
          <button
            onClick={() => {
              onOpened();
              setTimeout(() => scrollToRef?.current?.scrollIntoView(true));
            }}
            className="text-[14px] text-black border border-black py-1 px-3 rounded-full h-fit">
            Get quote
          </button>
        )}
        {installer.submitted && (
          <div className="flex items-center">
            <img alt="" src={iconSubmitted} className="mr-1" />
            <p className="font-bold text-[14px] pt-1">Submitted</p>
          </div>
        )}
      </div>
      <div
        className={`${
          isOpen
            ? 'max-h-[1000px] transition-[max-height] duration-1000'
            : 'max-h-0'
        } overflow-hidden`}>
        <hr className="my-6"></hr>
        <p className="text-[18px]">Everything looks alright?</p>
        <p className="text-[16px] mt-4">
          Please review your details below, if everything looks good click
          “submit” and we will share your information with the installer.
        </p>
        <p className="text-[16px] mt-4">Full name</p>
        <Input
          defaultValue={fullName}
          placeholder="First and last name"
          errorMessage="Please enter first and last name"
          haveError={nameHaveError}
          onChange={(e: any) => handleNameInput(e)}
          fontSize={16}></Input>
        <p className="text-[16px] mt-4">Email address</p>
        <Input
          type="email"
          defaultValue={email}
          placeholder="Enter your email address"
          errorMessage="Please enter a valid email address"
          haveError={emailHaveError}
          onChange={(e: any) => handleEmailInput(e)}
          fontSize={16}></Input>
        <p className="text-[16px] mt-4">Phone Number</p>
        <Input
          type="tel"
          value={phoneNumber}
          placeholder="Enter your phone number"
          errorMessage="Please enter your phone number"
          haveError={phoneHaveError}
          onChange={(e: any) => handlePhoneInput(e)}
          fontSize={16}></Input>
        <p className="text-[16px] mt-4">Best time to call</p>
        <div className="w-full short:w-[350px] md-max:max-w-[279px] h-10 md:h-[57px] mb-6 md:mb-10 text-[14px]">
          <CustomSelect
            options={callOptions}
            value={callAt}
            onChange={(selectedOption: any) => setCallAt(selectedOption)}
            smallOptionHeight={true}></CustomSelect>
        </div>
        {!!otherInstallersList.length && (
          <div>
            <p className="text-[16px] mt-4">I would also like a quote from:</p>
            <div className="text-[14px] font-bold mt-2">
              {otherInstallersList.map((otherInstaller: any, index: number) => {
                return (
                  <div
                    key={index}
                    onClick={() => setInstallerSelected(otherInstaller.id)}
                    className="cursor-pointer flex my-3 select-none items-center">
                    <img
                      alt=""
                      src={
                        otherInstaller.selected ? iconCircleChecked : iconCircle
                      }
                      className="h-6 w-6 mr-3"
                    />
                    <p>{otherInstaller.company_name}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {submitError && (
          <p
            className={`text-enph-red-functional text-[12px] md:text-[18px] bg-enph-grey-04 rounded-[10px] p-4 md:p-[20px] my-4 md:my-5`}>
            Oops, something went wrong when submitting the data. Please try
            again.
          </p>
        )}
        <div className="flex my-6">
          <div className="w-[47%]">
            <Button
              title="Submit"
              isLoading={!installer.submitted && submitting}
              disabled={
                installer.submitted ||
                submitting ||
                nameHaveError ||
                emailHaveError ||
                phoneHaveError
              }
              className="!w-full short:!w-full"
              action={submit}></Button>
          </div>
          <div className="w-[6%]"></div>
          <div className="w-[47%]">
            <Button
              title="Cancel"
              action={() => {
                onClosed();
                setTimeout(scrollToRef?.current?.scrollIntoView(true));
              }}
              disabled={submitting}
              className="!w-full short:!w-full bg-transparent !text-black border border-black md:hover:bg-transparent"></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const InstallerRating = ({ rating }: { rating: number }) => {
  if (!rating) return <></>;

  const ratingStars: RatingStar[] = new Array(5).fill('empty');
  let filledStars = Math.floor(rating);
  if (filledStars > 5) filledStars = 5;
  for (let i = 0; i < filledStars; i++) {
    ratingStars[i] = 'filled';
  }
  if (rating > filledStars && filledStars !== 5)
    ratingStars[filledStars] = 'semiFilled';

  return (
    <div className="inline-flex h-fit items-center">
      <p className="text-[14px] pt-[0.4rem] font-bold">{rating.toFixed(1)}</p>
      <div className="inline-flex ml-1">
        {ratingStars.map((star, index) => (
          <img
            key={index}
            alt=""
            className="h-3 w-3"
            src={
              star === 'empty'
                ? ratingStarEmpty
                : star === 'filled'
                ? ratingStarFilled
                : ratingStarSemiFilled
            }
          />
        ))}
      </div>
    </div>
  );
};

export const Graph25Years = ({
  data = null,
  billOver25Years,
  billOver25YearsSystemOne,
  billOver25YearsSystemTwo,
  itcSolarOnly,
  itcSolarAndBattery,
}: {
  data?: any;
  billOver25Years: number;
  billOver25YearsSystemOne: number;
  billOver25YearsSystemTwo: number;
  itcSolarOnly: number;
  itcSolarAndBattery: number;
}) => {
  const [selectedPaymentOption, setSelectedPaymentOption] = useState('cash');
  const [selectedSolarOption, setSelectedSolarOption] = useState({
    value: 'solarOnly',
    label: 'Solar only',
  });

  const handleSelectChange = (selectedOption: any) => {
    setSelectedSolarOption(selectedOption);
  };

  return (
    <div>
      <p className="text-[32px]">
        {selectedPaymentOption === 'cash'
          ? 'Own your system, maximize savings'
          : 'Own your system, no up-front cost'}
      </p>
      <div className="mt-10 flex h-[48px] gap-8">
        <ToggleSelector
          options={['cash', 'loan']}
          selectedOption={selectedPaymentOption}
          setSelectedOption={setSelectedPaymentOption}
          useWhiteBackground={true}
        />
        <CustomSelect
          value={selectedSolarOption}
          onChange={handleSelectChange}
          options={[
            { value: 'solarOnly', label: 'Solar only' },
            { value: 'solarAndBattery', label: 'Solar & battery' },
          ]}
          noBorder={true}
        />
      </div>
      <div className="mt-10 flex gap-[48px]">
        <div
          id="graph-25-years-wrapper"
          className="p-10 bg-white rounded-[12px] shrink-0">
          {data && (
            <>
              <Graph25YearsBars
                data={
                  data[selectedPaymentOption][selectedSolarOption.value]
                }></Graph25YearsBars>
            </>
          )}
        </div>
        <div className="flex flex-col">
          <p className="text-[18px] xl:text-[24px]">
            {formatNumber(billOver25Years)}
          </p>
          <p className="text-[14px] xl:text-[16px]">
            Estimated electricity bills over 25 years without solar
          </p>
          <hr className="my-6"></hr>
          <p className="text-[18px] xl:text-[24px]">
            {formatNumber(
              selectedSolarOption.value === 'solarOnly'
                ? billOver25Years - billOver25YearsSystemOne
                : billOver25Years - billOver25YearsSystemTwo
            )}
          </p>
          <p className="text-[14px] xl:text-[16px]">
            Estimated bill savings from adding solar{' '}
            {selectedSolarOption.value !== 'solarOnly' && '& battery'}
          </p>
          <hr className="my-6"></hr>
          {data &&
            data[selectedPaymentOption][selectedSolarOption.value]
              .yearlyLoan && (
              <>
                <p className="text-[18px] xl:text-[24px]">
                  {formatNumber(
                    data[selectedPaymentOption][selectedSolarOption.value]
                      .yearlyLoan * 20
                  )}
                </p>
                <p className="text-[14px] xl:text-[16px]">
                  Loan repayments over 20 years*
                </p>
                <hr className="my-6"></hr>
              </>
            )}
          <p className="text-[18px] xl:text-[24px]">
            {formatNumber(
              selectedSolarOption.value === 'solarOnly'
                ? billOver25YearsSystemOne
                : billOver25YearsSystemTwo
            )}
          </p>
          <p className="text-[14px] xl:text-[16px]">
            Estimated electricity bills over 25 years with solar{' '}
            {selectedSolarOption.value !== 'solarOnly' && '& battery'}
          </p>
        </div>
      </div>
      {selectedPaymentOption === 'loan' && (
        <p className="mt-4 text-[14px]">
          * After adjusting for initial pre-payment of incentive amount:{' '}
          {formatNumber(
            selectedSolarOption.value === 'solarOnly'
              ? itcSolarOnly
              : itcSolarAndBattery
          )}
        </p>
      )}
    </div>
  );
};

const Graph25YearsBars = ({ data }: { data: any }) => {
  if (!data) return null;

  return (
    <>
      <div className="h-[65%] flex justify-between graph-25-years-bars-wrapper">
        {data.current.map((currentAmount: number, index: number) => (
          <Graph25YearsBar
            key={index}
            currentAmount={currentAmount}
            newAmount={data.new[index]}
            maxAmount={data.current[24]}
            year={index + 1}
            loanAmount={data.yearlyLoan || null}></Graph25YearsBar>
        ))}
      </div>
      <p className="text-center text-[12px] mt-10">YEAR</p>
      <div className="flex justify-center items-center mt-4">
        <div className="bg-enph-orange w-[6px] h-[6px] rounded-full"></div>
        <p className="text-[12px] ml-2">CURRENT UTILITY BILL</p>
        <div className="bg-[#DCDCD6] w-[6px] h-[6px] rounded-full ml-6"></div>
        <p className="text-[12px] ml-2">ESTIMATED NEW UTILITY BILL</p>
        {data.yearlyLoan && (
          <>
            <div className="bg-[#F9DAA5] w-[6px] h-[6px] rounded-full ml-6"></div>
            <p className="text-[12px] ml-2">ANNUAL LOAN PAYMENT</p>
          </>
        )}
      </div>
    </>
  );
};

const Graph25YearsBar = ({
  currentAmount,
  newAmount,
  loanAmount = null,
  maxAmount,
  year,
}: {
  currentAmount: number;
  newAmount: number;
  loanAmount?: number | null;
  maxAmount: number;
  year: number;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="h-full flex gap-[4px] items-end relative graph-25-years-bar-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <div
        className={`${'bg-enph-orange'} w-[6px] rounded-t-full relative graph-25-years-bar`}
        style={{ height: `${(currentAmount / maxAmount) * 100}%` }}>
        {isHovered && (
          <>
            <div className="bg-enph-orange w-[20px] h-[20px] rounded-full absolute top-[-5px] left-[-7px] opacity-40"></div>
            <div className="bg-enph-orange w-[10px] h-[10px] rounded-full absolute top-0 left-[-2px]"></div>
            <div className="bg-white rounded-[8px] p-3 left-0 bottom-full absolute mb-4 flex flex-col border border-gray-200 z-10 w-max shadow-xl">
              <div className="flex items-center">
                <div className="bg-enph-orange w-[6px] h-[6px] rounded-full"></div>
                <p className="text-[10px] ml-2">CURRENT UTILITY BILL</p>
              </div>
              <p className="ml-3 text-[14px] text">
                {formatNumber(currentAmount)}
              </p>
              {loanAmount && year < 21 && (
                <>
                  <div className="flex items-center mt-2">
                    <div className="bg-[#F9DAA5] w-[6px] h-[6px] rounded-full"></div>
                    <p className="text-[10px] ml-2">ANNUAL LOAN PAYMENT</p>
                  </div>
                  <p className="ml-3 text-[14px] text">
                    {formatNumber(loanAmount)}
                  </p>
                </>
              )}
              <div className="flex items-center mt-2">
                <div className="bg-[#DCDCD6] w-[6px] h-[6px] rounded-full"></div>
                <p className="text-[10px] ml-2">ESTIMATED NEW UTILITY BILL</p>
              </div>
              <p className="ml-3 text-[14px] text">{formatNumber(newAmount)}</p>
            </div>
          </>
        )}
      </div>
      {!loanAmount && (
        <div
          className="bg-[#DCDCD6] w-[6px] rounded-t-full graph-25-years-bar"
          style={{ height: `${(newAmount / maxAmount) * 100}%` }}></div>
      )}
      {loanAmount && year < 21 && (
        <>
          <div
            className="bg-[#F9DAA5] w-[6px] rounded-t-full relative graph-25-years-bar"
            style={{
              height: `${((loanAmount + newAmount) / maxAmount) * 100}%`,
            }}></div>
          <div
            className="bg-[#DCDCD6] w-[6px] absolute bottom-0 right-0 graph-25-years-bar"
            style={{ height: `${(newAmount / maxAmount) * 100}%` }}></div>
        </>
      )}
      {loanAmount && year > 20 && (
        <>
          <div
            className="bg-[#DCDCD6] w-[6px] rounded-t-full graph-25-years-bar"
            style={{ height: `${(newAmount / maxAmount) * 100}%` }}></div>
        </>
      )}
      <div className="w-full absolute bottom-[-25px] text-center">
        <p className="text-[10px]">{`${year < 10 ? '0' : ''}${year}`}</p>
      </div>
    </div>
  );
};
